import React from "react";

import {
    Box,
    Typography,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
} from "@mui/material";

import {
    School,
    Person,
    Download,
    CalendarMonth,
    Assessment,
    EmojiEvents,
    Groups,
    Verified,
    EventAvailable,
    MenuBook,
} from "@mui/icons-material";

import testlogo from "./RoyalLogo.png";

/* ============================================================
   BRAND SYSTEM
============================================================ */

const COLORS = {
    deepNavy: "#031326",
    navy: "#061A33",
    royalNavy: "#0A2E5A",
    blue: "#124A8A",

    gold: "#D4A017",
    brightGold: "#F4C542",
    softGold: "#FFF7D6",

    white: "#FFFFFF",
    paper: "#FFFFFF",
    background: "#EEF2F6",

    text: "#172033",
    muted: "#667085",
    border: "#D7DEE8",
    softBlue: "#F3F7FC",

    danger: "#B42318",
    dangerSoft: "#FFF1F0",

    success: "#197A55",
    successSoft: "#ECFDF3",

    warning: "#A15C00",
    warningSoft: "#FFF7E6",
};

/* ============================================================
   HELPERS
============================================================ */

const formatValue = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "N/A";
    }

    return value;
};

const getNumericScore = (score) => {
    if (
        score === null ||
        score === undefined ||
        score === ""
    ) {
        return null;
    }

    const numeric = Number(score);

    return Number.isFinite(numeric)
        ? numeric
        : null;
};

const getScoreColor = (score) => {
    const value = getNumericScore(score);

    if (value !== null && value < 70) {
        return COLORS.danger;
    }

    return COLORS.navy;
};

const getScoreBackground = (score) => {
    const value = getNumericScore(score);

    if (value !== null && value < 70) {
        return COLORS.dangerSoft;
    }

    return COLORS.white;
};

const getScoreLabel = (score) => {
    const value = getNumericScore(score);

    if (value === null) {
        return "";
    }

    if (value >= 90) {
        return "Excellent";
    }

    if (value >= 80) {
        return "Very Good";
    }

    if (value >= 70) {
        return "Satisfactory";
    }

    return "Needs Improvement";
};

const getAverageStatus = (score) => {
    const value = getNumericScore(score);

    if (value === null) {
        return {
            label: "Not Available",
            color: COLORS.muted,
            background: "#F2F4F7",
        };
    }

    if (value >= 90) {
        return {
            label: "Excellent Performance",
            color: COLORS.success,
            background: COLORS.successSoft,
        };
    }

    if (value >= 80) {
        return {
            label: "Very Good Performance",
            color: COLORS.blue,
            background: COLORS.softBlue,
        };
    }

    if (value >= 70) {
        return {
            label: "Satisfactory Performance",
            color: COLORS.warning,
            background: COLORS.warningSoft,
        };
    }

    return {
        label: "Requires Academic Attention",
        color: COLORS.danger,
        background: COLORS.dangerSoft,
    };
};

/* ============================================================
   COMPONENT
============================================================ */

const ONScreenReportCard = ({
    reportCard,
    promotionChecks,
    handlePromotionCheckChange,
    selectedYear,
    handleDownloadStudentReportCard,
}) => {
    if (!reportCard) {
        return null;
    }

    const periods = [
        "Period 1",
        "Period 2",
        "Period 3",
        "Exam",
        "Period 4",
        "Period 5",
        "Period 6",
        "Exam2",
    ];

    const subjects = [
        ...new Set(
            (reportCard.period_grades || []).map(
                (item) => item.subject_name
            )
        ),
    ];

    const averageStatus = getAverageStatus(
        reportCard.yearly_average
    );

    return (
        <Box
            id="onscreen-report-card"
            sx={{
                width: "100%",
                background: COLORS.background,
                py: {
                    xs: 1,
                    sm: 2,
                    md: 4,
                },
                px: {
                    xs: 0,
                    sm: 1,
                    md: 2,
                },
                fontFamily:
                    '"Inter", "Roboto", "Arial", sans-serif',
                color: COLORS.text,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "1120px",
                    mx: "auto",
                    background: COLORS.paper,
                    border: {
                        xs: "none",
                        sm: `1px solid ${COLORS.border}`,
                    },
                    boxShadow: {
                        xs: "none",
                        sm: "0 18px 45px rgba(3,19,38,0.10)",
                    },
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* =====================================================
                    TOP OFFICIAL BORDER
                ====================================================== */}

                <Box
                    sx={{
                        height: {
                            xs: 5,
                            sm: 7,
                        },
                        background: `linear-gradient(
                            90deg,
                            ${COLORS.deepNavy} 0%,
                            ${COLORS.royalNavy} 55%,
                            ${COLORS.gold} 55%,
                            ${COLORS.brightGold} 100%
                        )`,
                    }}
                />

                {/* =====================================================
                    SCHOOL HEADER
                ====================================================== */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        pt: {
                            xs: 2.5,
                            sm: 3.5,
                        },
                        pb: {
                            xs: 2,
                            sm: 3,
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "120px 1fr 220px",
                            },
                            alignItems: "center",
                            gap: {
                                xs: 2,
                                md: 3,
                            },
                        }}
                    >
                        {/* LOGO */}

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: {
                                    xs: "center",
                                    md: "flex-start",
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src={testlogo}
                                alt="Royal Priesthood Academy"
                                sx={{
                                    width: {
                                        xs: 82,
                                        sm: 95,
                                        md: 108,
                                    },
                                    height: {
                                        xs: 82,
                                        sm: 95,
                                        md: 108,
                                    },
                                    objectFit: "contain",
                                    borderRadius: "50%",
                                    border: `3px solid ${COLORS.navy}`,
                                    p: 0.5,
                                    background: COLORS.white,
                                }}
                            />
                        </Box>

                        {/* SCHOOL INFORMATION */}

                        <Box
                            sx={{
                                textAlign: {
                                    xs: "center",
                                    md: "left",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 21,
                                        sm: 27,
                                        md: 31,
                                    },
                                    lineHeight: 1.15,
                                    fontWeight: 900,
                                    letterSpacing: "-0.5px",
                                    color: COLORS.deepNavy,
                                }}
                            >
                                Royal Priesthood Academy
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.8,
                                    fontSize: {
                                        xs: 11,
                                        sm: 12,
                                        md: 13,
                                    },
                                    lineHeight: 1.6,
                                    color: COLORS.muted,
                                    maxWidth: 650,
                                }}
                            >
                         New Jerusalem Community, Johnsonville, Monrovia, Liberia
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: COLORS.royalNavy,
                                }}
                            >
                                Academic Year:{" "}
                                {formatValue(selectedYear?.name)}
                            </Typography>
                        </Box>

                        {/* REPORT IDENTIFICATION */}

                        <Box
                            sx={{
                                border: `1px solid ${COLORS.border}`,
                                background: COLORS.softBlue,
                                p: {
                                    xs: 1.5,
                                    sm: 2,
                                },
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 10,
                                        sm: 11,
                                    },
                                    fontWeight: 800,
                                    letterSpacing: 1.5,
                                    color: COLORS.gold,
                                }}
                            >
                                OFFICIAL ACADEMIC RECORD
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: {
                                        xs: 16,
                                        sm: 19,
                                    },
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                STUDENT PROGRESS REPORT
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.4,
                                    fontSize: 11,
                                    color: COLORS.muted,
                                }}
                            >
                                Academic Performance &
                                Student Outcome Report
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            mt: 3,
                            height: 2,
                            background: COLORS.deepNavy,
                            position: "relative",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                width: 125,
                                height: 4,
                                background: COLORS.gold,
                                transform: "translateY(-1px)",
                            }}
                        />
                    </Box>
                </Box>

                {/* =====================================================
                    REPORT PURPOSE
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        px: {
                            xs: 1.5,
                            sm: 2,
                            md: 2.5,
                        },
                        py: {
                            xs: 1.5,
                            sm: 2,
                        },
                        borderLeft: `4px solid ${COLORS.gold}`,
                        background: "#FAFBFC",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: {
                                xs: 12,
                                sm: 13,
                            },
                            lineHeight: 1.7,
                            color: COLORS.muted,
                        }}
                    >
                        This academic report summarizes the student's
                        learning progress, assessment results and
                        overall academic outcome throughout the
                        reporting year. It is intended to provide
                        students, parents, teachers and school
                        leadership with a clear record of academic
                        achievement and areas requiring continued
                        development.
                    </Typography>
                </Box>

                {/* =====================================================
                    STUDENT INFORMATION
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.3,
                            background: COLORS.deepNavy,
                            color: COLORS.white,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Person fontSize="small" />

                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: 13,
                                letterSpacing: 0.5,
                            }}
                        >
                            STUDENT INFORMATION
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                            },
                        }}
                    >
                        {[
                            [
                                "Student Name",
                                formatValue(
                                    reportCard.student_name
                                ),
                            ],
                            [
                                "Student ID",
                                formatValue(
                                    reportCard.student_id
                                ),
                            ],
                            [
                                "Grade Class",
                                formatValue(
                                    reportCard.grade_class_name
                                ),
                            ],
                            [
                                "Academic Year",
                                formatValue(
                                    selectedYear?.name
                                ),
                            ],
                            [
                                "Reporting Cycle",
                                "Full Academic Year",
                            ],
                            [
                                "Student Status",
                                "Active Student",
                            ],
                        ].map(([label, value], index) => (
                            <Box
                                key={label}
                                sx={{
                                    px: {
                                        xs: 1.5,
                                        sm: 2,
                                    },
                                    py: 1.5,
                                    borderRight: {
                                        xs: "none",
                                        md:
                                            index % 3 !== 2
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                    },
                                    borderBottom: {
                                        xs: `1px solid ${COLORS.border}`,
                                        sm:
                                            index < 4
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.7,
                                        fontWeight: 800,
                                        color: COLORS.muted,
                                    }}
                                >
                                    {label}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: {
                                            xs: 13,
                                            sm: 14,
                                        },
                                        fontWeight: 700,
                                        color: COLORS.deepNavy,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* =====================================================
                    ACADEMIC PERFORMANCE
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <Assessment
                            sx={{
                                color: COLORS.gold,
                                fontSize: 22,
                            }}
                        />

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 17,
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                Academic Performance
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: COLORS.muted,
                                }}
                            >
                                Subject assessment results and
                                annual learning outcomes
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            height: 1,
                            background: COLORS.border,
                            mb: 1.5,
                        }}
                    />

                    {/* DESKTOP TABLE */}

                    <Box
                        sx={{
                            display: {
                                xs: "none",
                                md: "block",
                            },
                            border: `1px solid ${COLORS.border}`,
                        }}
                    >
                        <TableContainer>
                            <Table
                                size="small"
                                sx={{
                                    tableLayout: "fixed",
                                }}
                            >
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            background:
                                                COLORS.deepNavy,
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                width: "20%",
                                                color:
                                                    COLORS.white,
                                                fontWeight: 800,
                                                fontSize: 11,
                                                borderRight:
                                                    "1px solid rgba(255,255,255,.15)",
                                            }}
                                        >
                                            COURSE / SUBJECT
                                        </TableCell>

                                        {periods.map(
                                            (period) => (
                                                <TableCell
                                                    key={period}
                                                    align="center"
                                                    sx={{
                                                        color:
                                                            COLORS.white,
                                                        fontWeight: 800,
                                                        fontSize: 9,
                                                        px: 0.5,
                                                        borderRight:
                                                            "1px solid rgba(255,255,255,.15)",
                                                    }}
                                                >
                                                    {period
                                                        .replace(
                                                            "Period ",
                                                            "P"
                                                        )
                                                        .replace(
                                                            "Exam2",
                                                            "EXAM 2"
                                                        )}
                                                </TableCell>
                                            )
                                        )}

                                        <TableCell
                                            align="center"
                                            sx={{
                                                color:
                                                    COLORS.white,
                                                fontWeight: 800,
                                                fontSize: 9,
                                                background:
                                                    COLORS.royalNavy,
                                            }}
                                        >
                                            YEAR AVG
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {subjects.length > 0 ? (
                                        subjects.map(
                                            (
                                                subject,
                                                index
                                            ) => {
                                                const subjectGrades =
                                                    reportCard.period_grades.filter(
                                                        (item) =>
                                                            item.subject_name ===
                                                            subject
                                                    );

                                                const yearlyAverage =
                                                    reportCard.subjects_semester_averages?.find(
                                                        (item) =>
                                                            item.subject_name ===
                                                            subject
                                                    );

                                                return (
                                                    <TableRow
                                                        key={
                                                            subject
                                                        }
                                                        sx={{
                                                            background:
                                                                index %
                                                                    2 ===
                                                                0
                                                                    ? "#FBFCFE"
                                                                    : COLORS.white,
                                                        }}
                                                    >
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 800,
                                                                color: COLORS.deepNavy,
                                                                fontSize: 12,
                                                                borderRight: `1px solid ${COLORS.border}`,
                                                                borderBottom: `1px solid ${COLORS.border}`,
                                                                wordBreak:
                                                                    "break-word",
                                                            }}
                                                        >
                                                            {
                                                                subject
                                                            }
                                                        </TableCell>

                                                        {periods.map(
                                                            (
                                                                period
                                                            ) => {
                                                                const grade =
                                                                    subjectGrades.find(
                                                                        (
                                                                            item
                                                                        ) =>
                                                                            item.period_name ===
                                                                            period
                                                                    );

                                                                const score =
                                                                    grade?.formatted_final_score;

                                                                return (
                                                                    <TableCell
                                                                        key={
                                                                            period
                                                                        }
                                                                        align="center"
                                                                        sx={{
                                                                            p: 0.5,
                                                                            color:
                                                                                getScoreColor(
                                                                                    score
                                                                                ),
                                                                            fontWeight: 800,
                                                                            fontSize: 11,
                                                                            background:
                                                                                getScoreBackground(
                                                                                    score
                                                                                ),
                                                                            borderRight: `1px solid ${COLORS.border}`,
                                                                            borderBottom: `1px solid ${COLORS.border}`,
                                                                        }}
                                                                    >
                                                                        {
                                                                            score ||
                                                                            "-"
                                                                        }
                                                                    </TableCell>
                                                                );
                                                            }
                                                        )}

                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                fontWeight: 900,
                                                                fontSize: 12,
                                                                color: COLORS.deepNavy,
                                                                background: COLORS.softGold,
                                                                borderBottom: `1px solid ${COLORS.border}`,
                                                            }}
                                                        >
                                                            {yearlyAverage?.yearly_avg ||
                                                                "-"}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }
                                        )
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={10}
                                                align="center"
                                                sx={{
                                                    py: 4,
                                                    color: COLORS.muted,
                                                }}
                                            >
                                                No academic
                                                results are
                                                currently
                                                available.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* =================================================
                        MOBILE SUBJECT RESULTS
                    ================================================== */}

                    <Box
                        sx={{
                            display: {
                                xs: "block",
                                md: "none",
                            },
                        }}
                    >
                        {subjects.length > 0 ? (
                            subjects.map(
                                (subject, index) => {
                                    const subjectGrades =
                                        reportCard.period_grades.filter(
                                            (item) =>
                                                item.subject_name ===
                                                subject
                                        );

                                    const yearlyAverage =
                                        reportCard.subjects_semester_averages?.find(
                                            (item) =>
                                                item.subject_name ===
                                                subject
                                        );

                                    return (
                                        <Box
                                            key={subject}
                                            sx={{
                                                mb: 1.5,
                                                border: `1px solid ${COLORS.border}`,
                                                background:
                                                    COLORS.white,
                                            }}
                                        >
                                            {/* SUBJECT TITLE */}

                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 1.2,
                                                    background:
                                                        index %
                                                            2 ===
                                                        0
                                                            ? COLORS.softBlue
                                                            : "#F8FAFC",
                                                    borderBottom: `1px solid ${COLORS.border}`,
                                                    display:
                                                        "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems:
                                                        "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 900,
                                                        color: COLORS.deepNavy,
                                                        wordBreak:
                                                            "break-word",
                                                    }}
                                                >
                                                    {subject}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        flexShrink: 0,
                                                        fontSize: 9,
                                                        fontWeight: 800,
                                                        color: COLORS.muted,
                                                        textTransform:
                                                            "uppercase",
                                                    }}
                                                >
                                                    Subject
                                                </Typography>
                                            </Box>

                                            {/* PERIOD RESULTS */}

                                            <Box
                                                sx={{
                                                    display:
                                                        "grid",
                                                    gridTemplateColumns:
                                                        "repeat(2, 1fr)",
                                                }}
                                            >
                                                {periods.map(
                                                    (
                                                        period
                                                    ) => {
                                                        const grade =
                                                            subjectGrades.find(
                                                                (
                                                                    item
                                                                ) =>
                                                                    item.period_name ===
                                                                    period
                                                            );

                                                        const score =
                                                            grade?.formatted_final_score;

                                                        return (
                                                            <Box
                                                                key={
                                                                    period
                                                                }
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    justifyContent:
                                                                        "space-between",
                                                                    alignItems:
                                                                        "center",
                                                                    px: 1.25,
                                                                    py: 1,
                                                                    minWidth: 0,
                                                                    borderRight: `1px solid ${COLORS.border}`,
                                                                    borderBottom: `1px solid ${COLORS.border}`,
                                                                    background:
                                                                        getScoreBackground(
                                                                            score
                                                                        ),
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        color: COLORS.muted,
                                                                    }}
                                                                >
                                                                    {period
                                                                        .replace(
                                                                            "Period ",
                                                                            "P"
                                                                        )
                                                                        .replace(
                                                                            "Exam2",
                                                                            "Exam 2"
                                                                        )}
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 12,
                                                                        fontWeight: 900,
                                                                        color:
                                                                            getScoreColor(
                                                                                score
                                                                            ),
                                                                    }}
                                                                >
                                                                    {score ||
                                                                        "-"}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    }
                                                )}
                                            </Box>

                                            {/* YEAR AVERAGE */}

                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 1.25,
                                                    display:
                                                        "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems:
                                                        "center",
                                                    background:
                                                        COLORS.softGold,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 11,
                                                        fontWeight: 900,
                                                        color: COLORS.deepNavy,
                                                    }}
                                                >
                                                    Year Average
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        fontSize: 15,
                                                        fontWeight: 900,
                                                        color: COLORS.deepNavy,
                                                    }}
                                                >
                                                    {yearlyAverage?.yearly_avg ||
                                                        "-"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                }
                            )
                        ) : (
                            <Box
                                sx={{
                                    border: `1px solid ${COLORS.border}`,
                                    p: 3,
                                    textAlign: "center",
                                    color: COLORS.muted,
                                }}
                            >
                                No academic results are
                                currently available.
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* =====================================================
                    SEMESTER SUMMARY
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <CalendarMonth
                            sx={{
                                color: COLORS.gold,
                                fontSize: 22,
                            }}
                        />

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 17,
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                Academic Summary
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: COLORS.muted,
                                }}
                            >
                                Summary of performance across the
                                academic cycle
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(3, 1fr)",
                            },
                            border: `1px solid ${COLORS.border}`,
                        }}
                    >
                        {[
                            [
                                "Semester 1 Average",
                                reportCard.semester_1_avg,
                            ],
                            [
                                "Semester 2 Average",
                                reportCard.semester_2_avg,
                            ],
                            [
                                "Final Year Average",
                                reportCard.yearly_average,
                            ],
                        ].map(([label, value], index) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    px: 2,
                                    py: 2,
                                    borderRight: {
                                        xs: "none",
                                        sm:
                                            index < 2
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                    },
                                    borderBottom: {
                                        xs:
                                            index < 2
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                        sm: "none",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.6,
                                        fontWeight: 800,
                                        color: COLORS.muted,
                                    }}
                                >
                                    {label}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: {
                                            xs: 25,
                                            sm: 29,
                                        },
                                        lineHeight: 1,
                                        fontWeight: 900,
                                        color: COLORS.deepNavy,
                                    }}
                                >
                                    {value || "N/A"}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* =====================================================
                    OVERALL OUTCOME
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.3,
                            background: COLORS.deepNavy,
                            color: COLORS.white,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <EmojiEvents fontSize="small" />

                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 800,
                            }}
                        >
                            OVERALL ACADEMIC OUTCOME
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(3, 1fr)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                borderRight: {
                                    xs: "none",
                                    sm: `1px solid ${COLORS.border}`,
                                },
                                borderBottom: {
                                    xs: `1px solid ${COLORS.border}`,
                                    sm: "none",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: COLORS.muted,
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                Total Subjects
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 23,
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                {subjects.length}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderRight: {
                                    xs: "none",
                                    sm: `1px solid ${COLORS.border}`,
                                },
                                borderBottom: {
                                    xs: `1px solid ${COLORS.border}`,
                                    sm: "none",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: COLORS.muted,
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                Class Rank
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 23,
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                {reportCard.rank || "N/A"}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: COLORS.muted,
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                Total Students
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 23,
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                {reportCard.total_students ||
                                    "N/A"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* =====================================================
                    PERFORMANCE INTERPRETATION
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        p: {
                            xs: 1.75,
                            sm: 2.5,
                        },
                        background:
                            averageStatus.background,
                        border: `1px solid ${averageStatus.color}33`,
                        borderLeft: `5px solid ${averageStatus.color}`,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: {
                                xs: "flex-start",
                                sm: "center",
                            },
                            flexDirection: {
                                xs: "column",
                                sm: "row",
                            },
                            justifyContent:
                                "space-between",
                            gap: 1.5,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    textTransform:
                                        "uppercase",
                                    color: averageStatus.color,
                                    letterSpacing: 0.7,
                                }}
                            >
                                Academic Outcome
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.3,
                                    fontSize: 16,
                                    fontWeight: 900,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                {averageStatus.label}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                textAlign: {
                                    xs: "left",
                                    sm: "right",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: COLORS.muted,
                                    fontWeight: 700,
                                }}
                            >
                                FINAL YEAR AVERAGE
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 27,
                                    lineHeight: 1,
                                    fontWeight: 900,
                                    color: averageStatus.color,
                                }}
                            >
                                {reportCard.yearly_average ||
                                    "N/A"}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography
                        sx={{
                            mt: 1.5,
                            fontSize: 12,
                            lineHeight: 1.7,
                            color: COLORS.muted,
                        }}
                    >
                        The final academic average provides an
                        overall indication of the student's
                        achievement across the subjects and
                        assessment periods recorded in this report.
                        Continued progress should be supported
                        through regular study, attendance,
                        participation and appropriate academic
                        guidance.
                    </Typography>
                </Box>

                {/* =====================================================
                    TEACHER'S REMARK
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.3,
                            background: COLORS.softBlue,
                            borderBottom: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <MenuBook
                            sx={{
                                color: COLORS.royalNavy,
                                fontSize: 19,
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 900,
                                color: COLORS.deepNavy,
                            }}
                        >
                            TEACHER'S ACADEMIC REMARK
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            p: {
                                xs: 1.75,
                                sm: 2.5,
                            },
                            minHeight: 95,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                lineHeight: 1.8,
                                color: COLORS.text,
                            }}
                        >
                            {reportCard.remark ||
                                "No teacher remark has been recorded for this reporting period. Academic progress should continue to be monitored through classroom participation, assessment performance, attendance and regular communication between the school and home."}
                        </Typography>
                    </Box>
                </Box>

                {/* =====================================================
                    GRADE INTERPRETATION
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 16,
                            fontWeight: 900,
                            color: COLORS.deepNavy,
                            mb: 0.5,
                        }}
                    >
                        Grade Interpretation
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 11,
                            color: COLORS.muted,
                            mb: 1.5,
                        }}
                    >
                        General interpretation of reported
                        academic scores.
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, 1fr)",
                                sm: "repeat(5, 1fr)",
                            },
                            border: `1px solid ${COLORS.border}`,
                        }}
                    >
                        {[
                            [
                                "90–100",
                                "Excellent",
                                COLORS.success,
                            ],
                            [
                                "86–89",
                                "High Achievement",
                                COLORS.blue,
                            ],
                            [
                                "80–85",
                                "Strong Achievement",
                                COLORS.royalNavy,
                            ],
                            [
                                "70–79",
                                "Satisfactory",
                                COLORS.warning,
                            ],
                            [
                                "Below 70",
                                "Needs Improvement",
                                COLORS.danger,
                            ],
                        ].map(
                            ([range, label, color], index) => (
                                <Box
                                    key={range}
                                    sx={{
                                        p: 1.25,
                                        textAlign:
                                            "center",
                                        borderRight: {
                                            xs: index %
                                                        2 ===
                                                    0
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                            sm:
                                                index <
                                                4
                                                    ? `1px solid ${COLORS.border}`
                                                    : "none",
                                        },
                                        borderBottom: {
                                            xs:
                                                index <
                                                4
                                                    ? `1px solid ${COLORS.border}`
                                                    : "none",
                                            sm: "none",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 900,
                                            color,
                                        }}
                                    >
                                        {range}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.3,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: COLORS.muted,
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Box>
                </Box>

                {/* =====================================================
                    PROMOTION / ACADEMIC PROGRESSION
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        border: `1px solid ${COLORS.gold}`,
                        background: "#FFFCF2",
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.3,
                            background: COLORS.softGold,
                            borderBottom: `1px solid ${COLORS.gold}`,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 900,
                                color: COLORS.deepNavy,
                            }}
                        >
                            ACADEMIC PROGRESSION
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.3,
                                fontSize: 11,
                                color: COLORS.muted,
                            }}
                        >
                            Select the applicable academic
                            progression outcome after reviewing
                            the student's complete performance
                            record.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            p: {
                                xs: 1.5,
                                sm: 2,
                            },
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                            },
                            gap: 1,
                        }}
                    >
                        {[
                            ["promoted", "Promoted"],
                            [
                                "condition",
                                "Promoted With Condition",
                            ],
                            ["fail", "Failed"],
                            [
                                "neverToReturn",
                                "Never To Return",
                            ],
                        ].map(([name, label]) => (
                            <label
                                key={name}
                                style={{
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    gap: "9px",
                                    minHeight: "42px",
                                    padding:
                                        "9px 11px",
                                    border:
                                        "1px solid #E5D9B5",
                                    background:
                                        "#FFFFFF",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    color: COLORS.deepNavy,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    name={name}
                                    checked={
                                        promotionChecks?.[
                                            name
                                        ] || false
                                    }
                                    onChange={
                                        handlePromotionCheckChange
                                    }
                                    style={{
                                        width: 17,
                                        height: 17,
                                        accentColor:
                                            COLORS.royalNavy,
                                    }}
                                />

                                {label}
                            </label>
                        ))}
                    </Box>
                </Box>

                {/* =====================================================
                    ATTENDANCE & CONDUCT
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 3,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.3,
                            background: COLORS.deepNavy,
                            color: COLORS.white,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <EventAvailable fontSize="small" />

                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 900,
                            }}
                        >
                            ATTENDANCE & STUDENT DEVELOPMENT
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(3, 1fr)",
                            },
                        }}
                    >
                        {[
                            [
                                "Attendance",
                                reportCard.attendance ||
                                    "N/A",
                            ],
                            [
                                "Behavior",
                                reportCard.behavior ||
                                    "Excellent",
                            ],
                            [
                                "Participation",
                                reportCard.participation ||
                                    "Good",
                            ],
                        ].map(([label, value], index) => (
                            <Box
                                key={label}
                                sx={{
                                    p: 2,
                                    borderRight: {
                                        xs: "none",
                                        sm:
                                            index < 2
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                    },
                                    borderBottom: {
                                        xs:
                                            index < 2
                                                ? `1px solid ${COLORS.border}`
                                                : "none",
                                        sm: "none",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform:
                                            "uppercase",
                                        fontWeight: 800,
                                        color: COLORS.muted,
                                    }}
                                >
                                    {label}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: 14,
                                        fontWeight: 800,
                                        color: COLORS.deepNavy,
                                    }}
                                >
                                    {formatValue(value)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* =====================================================
                    AUTHORIZATION / SIGNATURES
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 4,
                        borderTop: `1px solid ${COLORS.border}`,
                        pt: 3,
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                            },
                            gap: {
                                xs: 4,
                                sm: 7,
                            },
                        }}
                    >
                        {[
                            "Class Teacher",
                            "School Administrator",
                        ].map((label) => (
                            <Box key={label}>
                                <Box
                                    sx={{
                                        height: 35,
                                        borderBottom: `1px solid ${COLORS.deepNavy}`,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        mt: 0.8,
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: COLORS.deepNavy,
                                    }}
                                >
                                    {label}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        color: COLORS.muted,
                                    }}
                                >
                                    Signature & Date
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* =====================================================
                    DOWNLOAD AREA
                ====================================================== */}

                <Box
                    sx={{
                        mx: {
                            xs: 2,
                            sm: 3,
                            md: 5,
                        },
                        mb: 4,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={
                            handleDownloadStudentReportCard
                        }
                        sx={{
                            minHeight: 46,
                            px: {
                                xs: 2.5,
                                sm: 4,
                            },
                            borderRadius: "4px",
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: 13,
                            background:
                                COLORS.deepNavy,
                            boxShadow:
                                "0 8px 18px rgba(3,19,38,.18)",
                            "&:hover": {
                                background:
                                    COLORS.royalNavy,
                            },
                        }}
                    >
                        Download Student Report Card
                    </Button>
                </Box>

                {/* =====================================================
                    FOOTER
                ====================================================== */}

                <Box
                    sx={{
                        borderTop: `3px solid ${COLORS.gold}`,
                        background: COLORS.deepNavy,
                        px: 2,
                        py: 2.5,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: {
                                xs: 14,
                                sm: 16,
                            },
                            fontWeight: 900,
                            color: COLORS.white,
                        }}
                    >
                        Royal Priesthood Academy
                    </Typography>

                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: 10,
                            color: "#CBD5E1",
                        }}
                    >
                        Building Character • Inspiring Excellence
                        • Creating Leaders
                    </Typography>

                    <Typography
                        sx={{
                            mt: 1.2,
                            fontSize: 9,
                            color: "#94A3B8",
                        }}
                    >
                        Academic record generated through the
                        school's student management system.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default ONScreenReportCard;